<script setup lang="ts">
import type { Project, Resource } from '@/models'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { i18n } from '@/i18n'
import { useI18nStore } from '@/composition/i18nStore'
import LikeButton from '@/components//LikeButton.vue'
import RightChevron from '@/assets/chevronRight.svg'

const { getters: i18nGetters } = useI18nStore()
const t = i18n.global.t

const props = defineProps<{
  project: Project
  resources: Resource[]
  liked?: boolean
  mode: 'like' | 'menu' | 'none'
  highlight?: string[]
}>()

const emit = defineEmits<{
  (e: 'select', value: Project): void
  (e: 'edit', value: Project): void
  (e: 'like', value: string): void
  (e: 'delete', value: string): void
}>()

const menu = ref(false)
const menuPosition = ref({ top: 0, left: 0 })
const buttonRef = ref<HTMLElement | null>(null)

const dateAsLocaleString = computed(() => {
  const locale = i18nGetters.currentLocale.value ?? 'en-US'
  const date = props.project?.lastEdited
  if (!(date instanceof Date)) return ''
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  })
})

const resourceCount = computed(() => props.project.resources.length)
const answeredCount = computed(() => {
  const categoryTypesFound: Set<string> = new Set()
  props.resources.forEach((r) => {
    if (r.category) categoryTypesFound.add(r.category)
  })
  return categoryTypesFound.size
})

function toggleMenu(open: boolean) {
  menu.value = open
  if (open && buttonRef.value) {
    const rect = buttonRef.value.getBoundingClientRect()
    menuPosition.value = {
      top: rect.bottom,
      left: rect.left,
    }
  }
}

function handleClickOutside() {
  menu.value = false
}

onMounted(() => {
  document.addEventListener('pointerup', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('pointerup', handleClickOutside)
})
</script>

<template>
  <div
    class="flex flex-col items-center text-center w-full py-6 rounded-2xl border border-[#DBD2D2] shadow-md/30 bg-white sm:aspect-446/570"
  >
    <div class="w-full flex flex-col items-start px-4 text-left grow">
      <p class="text-xl font-semibold">{{ project.title }}</p>
      <p class="font-normal mt-2">
        <span>{{ t('lastEdited') }}</span
        >:&nbsp;{{ dateAsLocaleString }}
      </p>
      <p class="text-md font-light mt-4 mb-2">{{ project.description }}</p>
    </div>
    <div
      class="bg-[#006E6821] w-full px-14 py-6 flex flex-col sm:flex-row sm:flex-wrap justify-center gap-x-10"
      :class="highlight"
    >
      <div class="max-sm:flex max-sm:items-center">
        <p class="text-lg font-normal">{{ t('resources') }}</p>
        <p class="text-3xl max-sm:text-lg text-black font-light max-sm:ml-2 sm:mt-2">
          {{ resourceCount }}
        </p>
      </div>
      <div class="max-sm:flex max-sm:items-center">
        <p class="text-lg font-normal">{{ t('answered') }}</p>
        <p class="text-3xl max-sm:text-lg text-black font-light max-sm:ml-2 sm:mt-2">
          {{ answeredCount }}/6
        </p>
      </div>
    </div>
    <div class="w-full flex flex-row items-center justify-between p-4 pb-0 self-end p-6">
      <div
        v-show="mode !== 'none'"
        class="flex flex-row items-center w-full"
        @click="emit('select', project)"
      >
        <p class="text-lg tracking-wider font-bold max-lg:hidden">{{ t('viewProject') }}</p>
        <img :src="RightChevron" class="w-4 h-4 lg:ml-4" />
      </div>
      <div class="flex flex-row">
        <div
          v-if="mode === 'like'"
          @click="emit('like', props.project.id)"
          class="ml-2 h-4 rounded-full flex items-center justify-center cursor-pointer"
        >
          <LikeButton class="w-6 h-6" :liked="liked" />
        </div>
        <div
          v-if="mode === 'menu'"
          ref="buttonRef"
          @pointerdown.prevent="toggleMenu(true)"
          @pointerup.prevent="toggleMenu(false)"
          class="ml-2 h-4 rounded-full flex items-center justify-center cursor-pointer"
        >
          <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </svg>
        </div>
      </div>
    </div>

    <!-- The menu is marked 'fixed' to avoid problems with parent overlap -->
    <div
      v-if="menu"
      class="fixed rounded-2xl p-4 flex flex-col items-start bg-white min-w-32 border shadow-lg z-50 cursor-pointer"
      :style="{
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
      }"
    >
      <p
        class="hover:bg-black hover:text-white whitespace-nowrap rounded-sm py-1 px-2"
        @pointerup.prevent="emit('edit', props.project)"
      >
        {{ t('editProject') }}
      </p>
      <p
        class="hover:bg-black hover:text-white whitespace-nowrap rounded-sm py-1 px-2"
        @pointerup.prevent="emit('delete', props.project.id)"
      >
        {{ t('removeProject') }}
      </p>
    </div>
  </div>
</template>
